import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MdFilterAltOff } from 'react-icons/md';
import Select from 'components/Common/Select';
import { getLanguageNameByCode, titleCase } from 'utils/helperFunctions';
import { Box, Drawer, Typography, Stack, useTheme, Button, IconButton } from '@mui/material';
import { IoClose } from 'react-icons/io5';
import VoiceCard from './VoiceCard';
import VoiceCardLoader from './VoiceCardLoader';
import { EmptyStateView } from 'components/Common/EmptyStateView';
import { LibraryVoice } from './types';
import { BuilderEmptyStateView } from '../../BuilderEmptyStateView';

// 'designed' = Kickcall's recommended ElevenLabs collection (fixed list, no paging or filters).
const LIBRARY_TABS = [
  { label: 'Recommended', value: 'designed' },
  { label: 'Explore', value: 'explore' }
];
const DEFAULT_LIBRARY = LIBRARY_TABS[0].value;

interface FilterFormData {
  library: string;
  language: string;
  accent: string;
  gender: string;
  age: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  voicesHook: any;
  languageAccentHook: any;
  selectedVoice: any;
  languages?: string[];
  onSelect: (voice: LibraryVoice) => void;
}

const VoiceListingModal: React.FC<Props> = ({
  open,
  onClose,
  voicesHook,
  languageAccentHook,
  onSelect,
  selectedVoice,
  languages = []
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);

  const { control, reset, handleSubmit, setValue, watch } = useForm<FilterFormData>({
    defaultValues: {
      library: DEFAULT_LIBRARY,
      language: languages[0] || '',
      accent: '',
      gender: '',
      age: ''
    }
  });

  const selectedLibrary = watch('library');

  // store applied filters
  const [appliedFilters, setAppliedFilters] = useState<FilterFormData>({
    library: DEFAULT_LIBRARY,
    language: languages[0] || '',
    accent: '',
    gender: '',
    age: ''
  });

  // call API with applied filters + page
  const { data: voicesData, isLoading: isVoicesLoading } = voicesHook({ page, filter: appliedFilters });
  const { data: languageAccents, isLoading: isAccentLoading } = languageAccentHook(watch('language'));

  const [voices, setVoices] = useState<LibraryVoice[]>([]);
  const isExplore = appliedFilters.library === 'explore';
  const hasMore = isExplore && Boolean(voicesData?.data?.has_more);

  useEffect(() => {
    if (voicesData?.data?.voices) {
      setVoices((prev) => {
        if (page === 0) {
          return voicesData.data.voices;
        }
        const existingVoiceIds = new Set(prev.map((v) => v.voice_id));
        const newVoices = voicesData.data.voices.filter((v: any) => !existingVoiceIds.has(v.voice_id));
        return [...prev, ...newVoices];
      });
    }
  }, [voicesData, page]);

  // apply filters on search
  const handleApplyFilters = handleSubmit((data) => {
    setPage(0); // reset page
    setAppliedFilters(data);
  });

  const handleClearFilters = () => {
    reset({
      library: appliedFilters.library,
      language: languages[0],
      accent: '',
      gender: '',
      age: ''
    });
    setPage(0);
    setAppliedFilters({
      library: appliedFilters.library,
      language: languages[0],
      accent: '',
      gender: '',
      age: ''
    });
  };

  const handleLibraryChange = (library: string) => {
    if (library === appliedFilters.library) return;
    setValue('library', library);
    setPage(0);
    setVoices([]);
    setAppliedFilters((prev) => ({ ...prev, library }));
  };

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const bottom = scrollHeight - scrollTop <= clientHeight + 2;
      if (bottom && !isVoicesLoading && hasMore) {
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, isVoicesLoading]
  );

  const isRecommendedTab = appliedFilters.library === 'designed';

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 800 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 100001
        }
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography variant='h6' fontWeight={500}>
          Select Voice
        </Typography>
        <IconButton onClick={onClose} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, pt: 2, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            bgcolor: '#fff',
            p: '4px',
            borderRadius: '4px',
            border: '1px solid #ECECEC'
          }}
        >
          {LIBRARY_TABS.map((tab) => {
            const isActive = appliedFilters.library === tab.value;
            return (
              <Box
                key={tab.value}
                onClick={() => handleLibraryChange(tab.value)}
                sx={{
                  flex: 1,
                  py: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontWeight: 500,
                  fontSize: 14,
                  bgcolor: isActive ? 'primary.light' : 'transparent',
                  color: isActive ? 'primary.main' : 'text.secondary',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: isActive ? 'primary.light' : 'rgba(0,0,0,0.03)'
                  }
                }}
              >
                {tab.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          border: !isRecommendedTab ? '1px solid #ECECEC' : '',
          mx: !isRecommendedTab ? 3 : 0,
          my: 2,
          borderRadius: '4px',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Fixed Filters (Explore only; Recommended is a fixed list) */}
        <Box sx={{ px: 3, pb: 1, pt: 2, flexShrink: 0, display: isExplore ? 'block' : 'none' }}>
          <form>
            <Stack direction='column' gap={2}>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                sx={{ minHeight: 'fit-content' }}
              >
                <Box display='flex' flexDirection='row' gap={2}>
                  {selectedLibrary === 'explore' && (
                    <>
                      <Box sx={{ minWidth: 100 }}>
                        <Select
                          name='language'
                          disabled={languages?.length < 2}
                          control={control}
                          label='Language'
                          size='small'
                          onChange={(val) => {
                            setValue('language', String(val?.target?.value));
                            setValue('accent', '');
                          }}
                          options={
                            languages.map((langCode) => ({
                              label: getLanguageNameByCode(langCode),
                              value: langCode
                            })) ?? []
                          }
                          sx={{ minWidth: 100 }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 100 }}>
                        <Select
                          name='accent'
                          control={control}
                          disabled={isAccentLoading}
                          label='Accent'
                          size='small'
                          options={
                            languageAccents?.data?.map((accent: string) => ({
                              label: titleCase(accent),
                              value: accent
                            })) ?? []
                          }
                          sx={{ minWidth: 100 }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 100 }}>
                        <Select
                          name='gender'
                          control={control}
                          label='Gender'
                          size='small'
                          options={[
                            { label: 'Male', value: 'male' },
                            { label: 'Female', value: 'female' },
                            { label: 'Neutral', value: 'neutral' }
                          ]}
                          sx={{ minWidth: 100 }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 100 }}>
                        <Select
                          name='age'
                          control={control}
                          label='Age'
                          size='small'
                          options={[
                            { label: 'Young', value: 'young' },
                            { label: 'Middle Aged', value: 'middle_aged' },
                            { label: 'Old', value: 'old' }
                          ]}
                          sx={{ minWidth: 100 }}
                        />
                      </Box>
                    </>
                  )}
                </Box>
                <Box display='flex' gap={1} alignItems='center'>
                  <Button onClick={handleApplyFilters} variant='contained' size='small'>
                    Search
                  </Button>
                  <MdFilterAltOff
                    color={theme.palette.primary.main}
                    onClick={handleClearFilters}
                    size='30px'
                    style={{ cursor: 'pointer' }}
                  />
                </Box>
              </Stack>
            </Stack>
          </form>
        </Box>

        {/* Scrollable Voices List */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 1, mr: 1 }} onScroll={handleScroll}>
          {languages?.length === 0 ? (
            <EmptyStateView message='Choose a language to see available voices' />
          ) : isVoicesLoading && voices.length === 0 ? (
            Array.from({ length: 4 }).map((_, index) => <VoiceCardLoader key={`voice-loader-${index}`} />)
          ) : voices.length > 0 ? (
            <>
              {voices.map((voice) => (
                <VoiceCard
                  key={voice.voice_id}
                  isDefaultVoice={!isExplore}
                  voice={voice}
                  listingView
                  isSelected={selectedVoice?.voice_id === voice.voice_id}
                  onSelect={onSelect}
                />
              ))}
              {isVoicesLoading && (
                <Typography color='secondary' align='center' pt={3}>
                  loading voices...
                </Typography>
              )}
              {isExplore && !hasMore && !isVoicesLoading && (
                <Typography color='secondary' align='center' pt={3}>
                  No more voices present
                </Typography>
              )}
            </>
          ) : (
            !isVoicesLoading && <BuilderEmptyStateView message='No Voice Found' />
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default VoiceListingModal;
